/*========================================================
 VIDA ESTILIO — PHOTO TRY-ON PRO v9.0
 Shopify Dawn | Capture Once → Try Any Frame
========================================================*/

(() => {
  "use strict";

  const CDN_BASE =
    "https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@0.4.1633559619";

  const DB_NAME = "vidaEstilioTryOn";
  const DB_VERSION = 1;
  const STORE_NAME = "faceProfile";
  const FACE_PROFILE_KEY = "primary";

  const DEFAULT_CALIBRATION = {
    scale: 1.26,
    verticalOffset: 0.01,
    horizontalOffset: 0,
    angleOffset: 0,
    heightRatioMin: 0.24,
    heightRatioMax: 0.62,
    maxCanvasWidthRatio: 0.80,
    minCanvasWidthRatio: 0.22
  };

  const SHAPE_PRESETS = {
    round:      { scale: 1.24, verticalOffset:  0.015, heightMultiplier: 1.00 },
    square:     { scale: 1.23, verticalOffset: -0.005, heightMultiplier: 0.96 },
    rectangle:  { scale: 1.22, verticalOffset: -0.015, heightMultiplier: 0.88 },
    aviator:    { scale: 1.24, verticalOffset: -0.010, heightMultiplier: 1.02 },
    "cat-eye":  { scale: 1.19, verticalOffset: -0.045, heightMultiplier: 0.92 },
    geometric:  { scale: 1.22, verticalOffset: -0.010, heightMultiplier: 0.98 },
    wayfarer:   { scale: 1.22, verticalOffset: -0.005, heightMultiplier: 0.94 },
    default:    { scale: 1.23, verticalOffset:  0.000, heightMultiplier: 1.00 }
  };

  const state = {
    modal: null,
    captureView: null,
    previewView: null,
    loading: null,
    error: null,
    status: null,
    video: null,
    previewCanvas: null,
    previewCtx: null,
    stream: null,
    faceMesh: null,
    captureLoop: null,
    processing: false,
    latestLandmarks: null,
    stableLandmarks: null,
    lastFaceSeenAt: 0,
    profile: null,
    frameImage: null,
    frameUrl: "",
    snapshot: null,
    previousBodyOverflow: ""
  };

  const $ = (selector, root = document) => root.querySelector(selector);

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function show(el) {
    el?.classList.remove("ve-hidden");
  }

  function hide(el) {
    el?.classList.add("ve-hidden");
  }

  function setStatus(message = "") {
    if (state.status) state.status.textContent = message;
  }

  function setError(message) {
    hide(state.loading);
    if (state.error) {
      const target = $(".ve-error-message", state.error);
      if (target) target.textContent = message;
      show(state.error);
    }
  }

  function clearError() {
    hide(state.error);
  }

  function normalizeShape(value = "") {
    const source = `${value} ${window.VTO_PRODUCT?.title || ""}`.toLowerCase();
    if (source.includes("cat")) return "cat-eye";
    if (source.includes("aviator")) return "aviator";
    if (source.includes("rectangle")) return "rectangle";
    if (source.includes("square")) return "square";
    if (source.includes("round")) return "round";
    if (source.includes("geometric") || source.includes("hex") || source.includes("oct")) return "geometric";
    if (source.includes("wayfarer")) return "wayfarer";
    return "default";
  }

  function getCalibration() {
    const product = window.VTO_PRODUCT || {};
    const shape = normalizeShape(
      product.measurements?.shape || product.shape || ""
    );
    const preset = SHAPE_PRESETS[shape] || SHAPE_PRESETS.default;
    const custom = product.calibration || {};
    const cleanCustom = {};

    Object.keys(custom).forEach((key) => {
      const value = custom[key];
      if (value !== null && value !== "" && value !== undefined) {
        const number = Number(value);
        cleanCustom[key] = Number.isFinite(number) ? number : value;
      }
    });

    return {
      ...DEFAULT_CALIBRATION,
      ...preset,
      horizontalOffset: 0,
      angleOffset: 0,
      ...cleanCustom,
      shape
    };
  }

  function getSelectedVariantId() {
    return (
      $('form[action*="/cart/add"] [name="id"]')?.value ||
      new URLSearchParams(window.location.search).get("variant") ||
      ""
    );
  }

  function getCurrentFrameUrl() {
    const product = window.VTO_PRODUCT || {};
    const variantId = getSelectedVariantId();

    if (variantId && product.variants?.[String(variantId)]) {
      return product.variants[String(variantId)];
    }

    return product.frameImage || product.image || "";
  }

  /* ---------- IndexedDB: save face photo across product pages ---------- */

  function openDatabase() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async function saveFaceProfile(profile) {
    const db = await openDatabase();

    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(profile, FACE_PROFILE_KEY);
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });

    db.close();
  }

  async function loadFaceProfile() {
    const db = await openDatabase();

    const profile = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const request = tx.objectStore(STORE_NAME).get(FACE_PROFILE_KEY);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });

    db.close();
    return profile;
  }

  async function deleteFaceProfile() {
    const db = await openDatabase();

    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).delete(FACE_PROFILE_KEY);
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });

    db.close();
  }

  /* ---------- Product image processing ---------- */

  function averageCornerColor(data, width, height) {
    const size = Math.max(4, Math.floor(Math.min(width, height) * 0.025));
    const corners = [
      [0, 0],
      [width - size, 0],
      [0, height - size],
      [width - size, height - size]
    ];

    let r = 0, g = 0, b = 0, count = 0;

    for (const [sx, sy] of corners) {
      for (let y = sy; y < sy + size; y++) {
        for (let x = sx; x < sx + size; x++) {
          const i = (y * width + x) * 4;
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
          count++;
        }
      }
    }

    return [r / count, g / count, b / count];
  }

  function removeBorderBackground(image) {
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(image, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const [bgR, bgG, bgB] = averageCornerColor(
      data,
      canvas.width,
      canvas.height
    );

    const width = canvas.width;
    const height = canvas.height;
    const visited = new Uint8Array(width * height);
    const queueX = new Int32Array(width * height);
    const queueY = new Int32Array(width * height);
    let head = 0;
    let tail = 0;

    const enqueue = (x, y) => {
      const p = y * width + x;
      if (visited[p]) return;
      visited[p] = 1;
      queueX[tail] = x;
      queueY[tail] = y;
      tail++;
    };

    for (let x = 0; x < width; x++) {
      enqueue(x, 0);
      enqueue(x, height - 1);
    }

    for (let y = 0; y < height; y++) {
      enqueue(0, y);
      enqueue(width - 1, y);
    }

    const tolerance = 26;
    const feather = 24;

    while (head < tail) {
      const x = queueX[head];
      const y = queueY[head];
      head++;

      const i = (y * width + x) * 4;
      const distance = Math.hypot(
        data[i] - bgR,
        data[i + 1] - bgG,
        data[i + 2] - bgB
      );

      if (distance > tolerance + feather) continue;

      if (distance <= tolerance) {
        data[i + 3] = 0;
      } else {
        const ratio = (distance - tolerance) / feather;
        data[i + 3] = Math.round(data[i + 3] * ratio);
      }

      if (x > 0) enqueue(x - 1, y);
      if (x < width - 1) enqueue(x + 1, y);
      if (y > 0) enqueue(x, y - 1);
      if (y < height - 1) enqueue(x, y + 1);
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
  }

  function cropVisiblePixels(canvas) {
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const width = canvas.width;
    const height = canvas.height;
    const data = ctx.getImageData(0, 0, width, height).data;

    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = data[(y * width + x) * 4 + 3];

        if (alpha > 18) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < minX || maxY < minY) return canvas;

    const cropW = maxX - minX + 1;
    const cropH = maxY - minY + 1;
    const padX = Math.round(cropW * 0.018);
    const padY = Math.round(cropH * 0.030);

    minX = Math.max(0, minX - padX);
    minY = Math.max(0, minY - padY);
    maxX = Math.min(width - 1, maxX + padX);
    maxY = Math.min(height - 1, maxY + padY);

    const finalW = maxX - minX + 1;
    const finalH = maxY - minY + 1;

    const output = document.createElement("canvas");
    output.width = finalW;
    output.height = finalH;

    output.getContext("2d").drawImage(
      canvas,
      minX,
      minY,
      finalW,
      finalH,
      0,
      0,
      finalW,
      finalH
    );

    return output;
  }

  function processFrameImage(image) {
    const source = document.createElement("canvas");
    source.width = image.naturalWidth || image.width;
    source.height = image.naturalHeight || image.height;

    const sourceCtx = source.getContext("2d", { willReadFrequently: true });
    sourceCtx.drawImage(image, 0, 0);

    const data = sourceCtx.getImageData(
      0,
      0,
      source.width,
      source.height
    ).data;

    let hasTransparency = false;

    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 250) {
        hasTransparency = true;
        break;
      }
    }

    const cleaned = hasTransparency
      ? source
      : removeBorderBackground(image);

    return cropVisiblePixels(cleaned);
  }

  async function loadFrameImage(url = getCurrentFrameUrl()) {
    if (!url) throw new Error("Product frame image missing.");

    if (state.frameImage && state.frameUrl === url) return state.frameImage;

    state.frameUrl = url;

    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.decoding = "async";
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Frame image could not be loaded."));
      img.src = url;
    });

    try {
      state.frameImage = processFrameImage(image);
    } catch (error) {
      console.warn("[Vida VTO] Background removal fallback:", error);
      state.frameImage = image;
    }

    return state.frameImage;
  }

  /* ---------- MediaPipe ---------- */

  async function ensureFaceMeshLibrary() {
    if (typeof window.FaceMesh === "function") return;

    await new Promise((resolve, reject) => {
      const existing = document.querySelector(
        'script[data-ve-mediapipe="face-mesh"]'
      );

      if (existing) {
        existing.addEventListener("load", resolve, { once: true });
        existing.addEventListener("error", reject, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = `${CDN_BASE}/face_mesh.js`;
      script.crossOrigin = "anonymous";
      script.dataset.veMediapipe = "face-mesh";
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    if (typeof window.FaceMesh !== "function") {
      throw new Error("Face tracking library could not be loaded.");
    }
  }

  async function initFaceMesh() {
    if (state.faceMesh) return;

    await ensureFaceMeshLibrary();

    state.faceMesh = new window.FaceMesh({
      locateFile: (file) => `${CDN_BASE}/${file}`
    });

    state.faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.62,
      minTrackingConfidence: 0.62
    });

    state.faceMesh.onResults((results) => {
      const detected = results.multiFaceLandmarks?.[0] || null;
      state.latestLandmarks = detected;

      if (detected && faceQualityOkay(detected)) {
        state.stableLandmarks = detected.map((point) => ({
          x: point.x,
          y: point.y,
          z: point.z || 0
        }));
        state.lastFaceSeenAt = Date.now();
      }
    });
  }

  function cameraErrorMessage(error) {
    switch (error?.name) {
      case "NotAllowedError":
      case "PermissionDeniedError":
        return "Camera permission denied. Please allow camera access in your browser settings.";
      case "NotFoundError":
      case "DevicesNotFoundError":
        return "No camera was found on this device.";
      case "NotReadableError":
      case "TrackStartError":
        return "The camera is being used by another app. Close it and try again.";
      case "SecurityError":
        return "A secure HTTPS connection is required to use the camera.";
      default:
        return "The camera could not start. Please check your permissions and browser settings.";
    }
  }

  async function startCamera() {
    state.latestLandmarks = null;
    state.stableLandmarks = null;
    state.lastFaceSeenAt = 0;
    await initFaceMesh();

    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("This browser does not support camera access.");
    }

    stopCamera();

    state.stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: "user",
        width: { ideal: 1280 },
        height: { ideal: 960 }
      }
    });

    state.video.srcObject = state.stream;

    await new Promise((resolve, reject) => {
      const timeout = window.setTimeout(
        () => reject(new Error("Camera loading timed out.")),
        12000
      );

      state.video.onloadedmetadata = async () => {
        try {
          await state.video.play();
          clearTimeout(timeout);
          resolve();
        } catch (error) {
          clearTimeout(timeout);
          reject(error);
        }
      };
    });

    startFaceScanLoop();
  }

  function stopCamera() {
    if (state.captureLoop) {
      cancelAnimationFrame(state.captureLoop);
      state.captureLoop = null;
    }

    state.processing = false;

    if (state.stream) {
      state.stream.getTracks().forEach((track) => track.stop());
      state.stream = null;
    }

    if (state.video) {
      state.video.pause();
      state.video.srcObject = null;
    }
  }

  function startFaceScanLoop() {
    const loop = async () => {
      if (
        state.stream &&
        !state.processing &&
        state.video?.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
        state.faceMesh
      ) {
        state.processing = true;

        try {
          await state.faceMesh.send({ image: state.video });
          const faceReady = faceQualityOkay(state.latestLandmarks);

          if (faceReady) {
            clearError();
            setStatus("Face detected — tap Capture Face.");
          } else {
            setStatus("Look straight at the camera and keep your face inside the guide.");
          }
        } catch (error) {
          console.error("[Vida VTO] Face scan:", error);
        } finally {
          state.processing = false;
        }
      }

      if (state.stream) {
        state.captureLoop = requestAnimationFrame(loop);
      }
    };

    state.captureLoop = requestAnimationFrame(loop);
  }

  /* ---------- Capture once ---------- */

  function faceQualityOkay(landmarks) {
    if (!landmarks) return false;

    const left = landmarks[234];
    const right = landmarks[454];
    const nose = landmarks[1];

    if (!left || !right || !nose) return false;

    const faceWidth = Math.abs(right.x - left.x);

    return (
      faceWidth > 0.10 &&
      nose.x > 0.10 &&
      nose.x < 0.90 &&
      nose.y > 0.10 &&
      nose.y < 0.90
    );
  }

  async function captureFace() {
    clearError();

    if (!state.video || state.video.readyState < 2) {
      setError("The camera is not ready yet.");
      return;
    }

    const captureLandmarks =
      faceQualityOkay(state.latestLandmarks)
        ? state.latestLandmarks
        : (
            state.stableLandmarks &&
            Date.now() - state.lastFaceSeenAt < 1500
              ? state.stableLandmarks
              : null
          );

    if (!captureLandmarks) {
      setError("Your face was not detected clearly. Keep your face inside the guide and tap Capture Face again.");
      return;
    }

    const width = state.video.videoWidth;
    const height = state.video.videoHeight;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    canvas.getContext("2d").drawImage(
      state.video,
      0,
      0,
      width,
      height
    );

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.92)
    );

    if (!blob) {
      setError("Your face photo could not be saved.");
      return;
    }

    const landmarks = captureLandmarks.map((point) => ({
      x: point.x,
      y: point.y,
      z: point.z || 0
    }));

    const profile = {
      version: 1,
      photoBlob: blob,
      width,
      height,
      landmarks,
      createdAt: Date.now()
    };

    await saveFaceProfile(profile);
    state.profile = profile;
    clearError();

    stopCamera();

    await renderPhotoTryOn();
  }

  /* ---------- Frame fitting on saved face photo ---------- */

  function point(landmarks, index, width, height) {
    const p = landmarks[index];
    return {
      x: p.x * width,
      y: p.y * height
    };
  }

  function midpoint(a, b) {
    return {
      x: (a.x + b.x) / 2,
      y: (a.y + b.y) / 2
    };
  }

  function distance(a, b) {
    return Math.hypot(b.x - a.x, b.y - a.y);
  }

  function measurementAdjustment(measurements = {}) {
    const lens = Number(measurements.lensWidth || 0);
    const bridge = Number(measurements.bridgeWidth || 0);
    const totalWidth = Number(measurements.frameWidth || 0);

    const estimatedWidth =
      totalWidth ||
      (lens && bridge ? lens * 2 + bridge + 10 : 0);

    return estimatedWidth
      ? clamp(estimatedWidth / 135, 0.90, 1.10)
      : 1;
  }

  function calculateFrameFit(landmarks, width, height, frameImage) {
    const calibration = getCalibration();
    const measurements = window.VTO_PRODUCT?.measurements || {};

    const templeL = point(landmarks, 234, width, height);
    const templeR = point(landmarks, 454, width, height);
    const eyeOuterL = point(landmarks, 33, width, height);
    const eyeOuterR = point(landmarks, 263, width, height);
    const eyeInnerL = point(landmarks, 133, width, height);
    const eyeInnerR = point(landmarks, 362, width, height);
    const noseBridge = point(landmarks, 168, width, height);

    const eyeCenterL = midpoint(eyeOuterL, eyeInnerL);
    const eyeCenterR = midpoint(eyeOuterR, eyeInnerR);
    const eyeCenter = midpoint(eyeCenterL, eyeCenterR);
    const templeCenter = midpoint(templeL, templeR);

    const templeWidth = distance(templeL, templeR);
    const eyeSpan = distance(eyeOuterL, eyeOuterR);
    const dimensionFactor = measurementAdjustment(measurements);

    const desiredWidth = clamp(
      Math.max(
        templeWidth * calibration.scale * dimensionFactor,
        eyeSpan * 1.76 * dimensionFactor
      ),
      width * calibration.minCanvasWidthRatio,
      width * calibration.maxCanvasWidthRatio
    );

    const naturalWidth =
      frameImage.naturalWidth || frameImage.width || 1;
    const naturalHeight =
      frameImage.naturalHeight || frameImage.height || 1;

    const naturalRatio = naturalHeight / naturalWidth;

    const desiredHeight =
      desiredWidth *
      clamp(
        naturalRatio,
        calibration.heightRatioMin,
        calibration.heightRatioMax
      ) *
      (calibration.heightMultiplier || 1);

    const angle =
      Math.atan2(
        eyeCenterR.y - eyeCenterL.y,
        eyeCenterR.x - eyeCenterL.x
      ) + calibration.angleOffset;

    const centerX =
      eyeCenter.x * 0.72 +
      templeCenter.x * 0.28 +
      desiredWidth * calibration.horizontalOffset;

    const bridgeDelta = noseBridge.y - eyeCenter.y;

    const centerY =
      eyeCenter.y +
      desiredHeight * calibration.verticalOffset +
      clamp(
        bridgeDelta * 0.04,
        -desiredHeight * 0.035,
        desiredHeight * 0.055
      );

    return {
      x: centerX,
      y: centerY,
      width: desiredWidth,
      height: desiredHeight,
      angle
    };
  }

  function drawFrame(ctx, frameImage, fit) {
    ctx.save();
    ctx.translate(fit.x, fit.y);
    ctx.rotate(fit.angle);
    ctx.drawImage(
      frameImage,
      -fit.width / 2,
      -fit.height / 2,
      fit.width,
      fit.height
    );
    ctx.restore();
  }

  async function blobToImage(blob) {
    const url = URL.createObjectURL(blob);

    try {
      return await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = url;
      });
    } finally {
      // URL must remain alive until image.onload fired; safe to revoke after await.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  }

  async function renderPhotoTryOn() {
    clearError();
    state.modal?.setAttribute("data-mode", "preview");
    hide(state.captureView);
    show(state.previewView);
    show(state.loading);
    setStatus("Preparing your frame…");

    try {
      if (!state.profile) {
        state.profile = await loadFaceProfile();
      }

      if (!state.profile?.photoBlob || !state.profile?.landmarks) {
        await showCaptureMode();
        return;
      }

      const [faceImage, frameImage] = await Promise.all([
        blobToImage(state.profile.photoBlob),
        loadFrameImage()
      ]);

      const width = state.profile.width || faceImage.naturalWidth;
      const height = state.profile.height || faceImage.naturalHeight;

      state.previewCanvas.width = width;
      state.previewCanvas.height = height;

      state.previewCtx.clearRect(0, 0, width, height);
      state.previewCtx.drawImage(faceImage, 0, 0, width, height);

      const fit = calculateFrameFit(
        state.profile.landmarks,
        width,
        height,
        frameImage
      );

      drawFrame(state.previewCtx, frameImage, fit);

      state.snapshot = state.previewCanvas.toDataURL("image/jpeg", 0.94);

      hide(state.loading);
      setStatus("Photo Try-On ready.");
    } catch (error) {
      console.error("[Vida VTO] Render error:", error);
      setError("The try-on preview could not be created. Please retake your face photo and try again.");
    }
  }

  /* ---------- UI modes ---------- */

  async function showCaptureMode() {
    state.modal?.setAttribute("data-mode", "capture");
    hide(state.previewView);
    show(state.captureView);
    show(state.loading);
    clearError();
    setStatus("Starting camera…");

    try {
      await startCamera();
      hide(state.loading);
      setStatus("Look straight at the camera and keep your face inside the guide.");
    } catch (error) {
      console.error("[Vida VTO] Camera:", error);
      setError(cameraErrorMessage(error));
    }
  }

  async function retakeFace() {
    stopCamera();
    state.profile = null;
    state.snapshot = null;
    await deleteFaceProfile();
    await showCaptureMode();
  }

  async function openVTO() {
    if (!state.modal) return;

    state.modal.classList.add("active");
    state.modal.setAttribute("aria-hidden", "false");

    state.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    clearError();
    state.modal.setAttribute("data-mode", "loading");
    state.profile = await loadFaceProfile();

    if (state.profile) {
      await renderPhotoTryOn();
    } else {
      await showCaptureMode();
    }
  }

  function closeVTO() {
    stopCamera();
    state.modal?.classList.remove("active");
    state.modal?.setAttribute("aria-hidden", "true");
    document.body.style.overflow = state.previousBodyOverflow;
  }

  /* ---------- Download / Share ---------- */

  function ensureSnapshot() {
    if (!state.snapshot && state.previewCanvas?.width) {
      state.snapshot = state.previewCanvas.toDataURL("image/jpeg", 0.94);
    }
    return state.snapshot;
  }

  function downloadPhoto() {
    const snapshot = ensureSnapshot();

    if (!snapshot) {
      setError("Please create a try-on preview first.");
      return;
    }

    const link = document.createElement("a");
    link.href = snapshot;
    link.download = `vida-estilio-photo-tryon-${Date.now()}.jpg`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  async function sharePhoto() {
    const snapshot = ensureSnapshot();

    if (!snapshot) {
      setError("Please create a try-on preview first.");
      return;
    }

    try {
      const blob = await (await fetch(snapshot)).blob();
      const file = new File(
        [blob],
        "vida-estilio-photo-tryon.jpg",
        { type: "image/jpeg" }
      );

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: window.VTO_PRODUCT?.title || "Vida Estilio Try-On",
          text: "My Vida Estilio virtual try-on"
        });
      } else {
        downloadPhoto();
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("[Vida VTO] Share:", error);
        downloadPhoto();
      }
    }
  }

  /* ---------- Variant updates ---------- */

  function observeVariantChanges() {
    document.addEventListener("change", (event) => {
      if (
        event.target.matches(
          'input[name="id"], select[name="id"], variant-selects select, variant-radios input'
        )
      ) {
        state.frameUrl = "";
        state.frameImage = null;

        if (
          state.modal?.classList.contains("active") &&
          state.profile
        ) {
          setTimeout(renderPhotoTryOn, 100);
        }
      }
    });

    document.addEventListener("variant:change", () => {
      state.frameUrl = "";
      state.frameImage = null;

      if (
        state.modal?.classList.contains("active") &&
        state.profile
      ) {
        setTimeout(renderPhotoTryOn, 100);
      }
    });
  }

  function bindEvents() {
    $("#ve-close-vto")?.addEventListener("click", closeVTO);
    $("#ve-capture-face")?.addEventListener("click", captureFace);
    $("#ve-retake-face")?.addEventListener("click", retakeFace);
    $("#ve-download")?.addEventListener("click", downloadPhoto);
    $("#ve-share")?.addEventListener("click", sharePhoto);
    $("#ve-retry")?.addEventListener("click", showCaptureMode);

    state.modal?.addEventListener("click", (event) => {
      if (event.target === state.modal) closeVTO();
    });

    document.addEventListener("keydown", (event) => {
      if (
        event.key === "Escape" &&
        state.modal?.classList.contains("active")
      ) {
        closeVTO();
      }
    });
  }

  function init() {
    state.modal = $("#ve-vto-modal");
    state.captureView = $("#ve-capture-view");
    state.previewView = $("#ve-preview-view");
    state.loading = $(".ve-loading", state.modal || document);
    state.error = $("#ve-error");
    state.status = $("#ve-status");
    state.video = $("#ve-video");
    state.previewCanvas = $("#ve-photo-canvas");

    if (
      !state.modal ||
      !state.video ||
      !state.previewCanvas
    ) {
      console.warn("[Vida VTO] Required elements missing.");
      return;
    }

    state.previewCtx = state.previewCanvas.getContext("2d", {
      alpha: false
    });

    bindEvents();
    observeVariantChanges();
  }

  window.openVTO = openVTO;
  window.closeVTO = closeVTO;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
